import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  LayoutDashboard, FileText, Users2, GraduationCap, PanelBottom,
  FormInput, MessageSquare, ShieldCheck, Mail, Menu, ExternalLink, LogOut, BellRing, Newspaper, ClipboardList, ListChecks
} from "lucide-react";
import { toast } from "sonner";
import AdminDashboard from "./AdminDashboard";
import AdminContent from "./AdminContent";
import AdminMembers from "./AdminMembers";
import AdminTrainings from "./AdminTrainings";
import AdminFooter from "./AdminFooter";
import AdminRegForm from "./AdminRegForm";
import AdminEventForm from "./AdminEventForm";
import AdminForum from "./AdminForum";
import AdminUsers from "./AdminUsers";
import AdminMessages from "./AdminMessages";
import AdminEmailSettings from "./AdminEmailSettings";
import AdminNews from "./AdminNews";
import AdminAppearance from "./AdminAppearance";
import AdminRegistrations from "./AdminRegistrations";

const MODULES = [
  { id: "dashboard", name: "Przegląd", icon: LayoutDashboard, comp: AdminDashboard },
  { id: "news", name: "Aktualności", icon: Newspaper, comp: AdminNews },
  { id: "content", name: "Treści podstron", icon: FileText, comp: AdminContent },
  { id: "appearance", name: "Pasek i galeria", icon: BellRing, comp: AdminAppearance },
  { id: "members", name: "Organy Izby", icon: Users2, comp: AdminMembers },
  { id: "trainings", name: "Konferencje i szkolenia", icon: GraduationCap, comp: AdminTrainings },
  { id: "registrations", name: "Uczestnicy konferencji", icon: ClipboardList, comp: AdminRegistrations },
  { id: "eventform", name: "Formularz konferencji", icon: ListChecks, comp: AdminEventForm },
  { id: "footer", name: "Edytor stopki", icon: PanelBottom, comp: AdminFooter },
  { id: "regform", name: "Formularz rejestracji", icon: FormInput, comp: AdminRegForm },
  { id: "forum", name: "Moderacja forum", icon: MessageSquare, comp: AdminForum },
  { id: "messages", name: "Wiadomości", icon: Mail, comp: AdminMessages },
  { id: "email", name: "Powiadomienia i ustawienia", icon: BellRing, comp: AdminEmailSettings },
  { id: "users", name: "Użytkownicy", icon: ShieldCheck, comp: AdminUsers },
];

export default function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const validIds = MODULES.map((m) => m.id);
  const initialModule = (() => {
    const m = new URLSearchParams(window.location.search).get("m");
    return validIds.includes(m) ? m : "dashboard";
  })();
  const [active, setActive] = useState(initialModule);
  const [open, setOpen] = useState(false);

  const ActiveComp = MODULES.find((m) => m.id === active).comp;

  const handleLogout = async () => { await logout(); toast.success("Wylogowano"); navigate("/"); };

  const Nav = ({ onNavigate }) => (
    <nav className="space-y-1">
      {MODULES.map((m) => (
        <button
          key={m.id}
          onClick={() => { setActive(m.id); onNavigate?.(); }}
          data-testid={`admin-nav-${m.id}`}
          className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-medium transition-colors ${
            active === m.id ? "bg-ztk-blue text-white" : "text-slate-300 hover:bg-white/5"
          }`}
        >
          <m.icon className="h-4 w-4 shrink-0" /> {m.name}
        </button>
      ))}
    </nav>
  );

  return (
    <div className="flex min-h-screen bg-background">
      {/* Sidebar desktop */}
      <aside className="fixed hidden h-screen w-64 flex-col bg-ztk-navydark p-4 lg:flex">
        <div className="mb-6 rounded-lg bg-white/95 p-2.5">
          <img src="/logo-ztkig.png" alt="ZTK" className="h-9 w-auto" />
        </div>
        <p className="label-mono mb-3 px-3 text-[10px] text-slate-500">Panel CRM / CMS</p>
        <div className="-mr-2 min-h-0 flex-1 overflow-y-auto pr-2">
          <Nav />
        </div>
        <div className="mt-4 space-y-2 border-t border-white/10 pt-4">
          <Button asChild variant="ghost" size="sm" className="w-full justify-start gap-2 text-slate-300 hover:bg-white/5">
            <Link to="/"><ExternalLink className="h-4 w-4" /> Zobacz stronę</Link>
          </Button>
          <Button variant="ghost" size="sm" onClick={handleLogout} className="w-full justify-start gap-2 text-slate-300 hover:bg-white/5" data-testid="admin-logout">
            <LogOut className="h-4 w-4" /> Wyloguj ({user?.name})
          </Button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 lg:ml-64">
        <header className="glass sticky top-0 z-30 flex h-14 items-center justify-between border-b border-border px-4 lg:hidden">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="admin-mobile-menu"><Menu className="h-5 w-5" /></Button>
            </SheetTrigger>
            <SheetContent side="left" className="flex w-64 flex-col overflow-y-auto bg-ztk-navydark p-4">
              <div className="mb-6 mt-2 rounded-lg bg-white/95 p-2.5"><img src="/logo-ztkig.png" alt="ZTK" className="h-9 w-auto" /></div>
              <Nav onNavigate={() => setOpen(false)} />
            </SheetContent>
          </Sheet>
          <span className="font-display font-semibold text-ztk-navy dark:text-white">Panel CRM</span>
          <div className="w-9" />
        </header>

        <main className="p-6 lg:p-10" data-testid="admin-content">
          <ActiveComp />
        </main>
      </div>
    </div>
  );
}
